import type { HexMapState } from '../../../engine/render/renderer';
import type { ContentBuilder } from '../../ui/ContentBuilder';
import { getColdestWarmth, getMaxWarmth } from '../../../engine/map/settlement';
import type { SettlementView } from '../utils';
import {
    getFoodHoursSupply,
    getHuntTicks,
    getLumberCampTarget,
    getTreeTotal,
    getWildlifeTotal,
} from '../utils';
import {
    ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET,
    ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET,
    ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX,
} from '../../../engine/input/actionIds';
import { t, type Language } from '../../../i18n/translations';
import {
    getBuildingStorage,
    getEffectiveInputCap,
    getEffectiveOutputCap,
} from '../../../engine/settlement/utils';
import {
    BoxItem,
    GridLayoutItem,
    LinearLayoutItem,
    ProgressTrackItem,
    SpacerItem,
    ThemedIconButtonItem,
    WrappedTextItem,
    type UiItem,
} from '../../ui/items';

export type ProductionTileEntry = {
    title: string;
    input: string;
    output: string;
    progressLabel?: string;
    progressValue?: number;
    progressMax?: number;
    warning?: boolean;
    settingsActionId?: string;
};

function buildProductionCells(entries: ProductionTileEntry[]): UiItem[] {
    return entries.map((entry) => {
        const titleBlock = new WrappedTextItem(entry.title, 'body');
        const titleItems: UiItem[] = [titleBlock];
        if (entry.settingsActionId) {
            titleItems.push(
                new ThemedIconButtonItem(
                    entry.settingsActionId,
                    'settings',
                    (t) => t.productionGrid.iconSize,
                ),
            );
        }

        const titleRow =
            titleItems.length > 1
                ? new LinearLayoutItem(titleItems, {
                      orientation: 'horizontal',
                      gap: 4,
                      width: 'fill',
                      weights: [1, 0],
                      crossGravity: 'start',
                  })
                : titleBlock;

        const hasTitle = entry.title.trim().length > 0;
        const hasInput = entry.input.trim().length > 0;
        const hasOutput = entry.output.trim().length > 0;
        const hasProgress =
            entry.progressLabel !== undefined &&
            entry.progressValue !== undefined &&
            entry.progressMax !== undefined;

        const contentItems: UiItem[] = [titleRow];
        if (hasTitle && (hasInput || hasOutput)) contentItems.push(new SpacerItem(2));
        if (hasInput) contentItems.push(new WrappedTextItem(entry.input, 'muted'));
        if (hasInput && hasOutput) contentItems.push(new SpacerItem(2));
        if (hasOutput) contentItems.push(new WrappedTextItem(entry.output, 'muted'));
        if (hasProgress) {
            contentItems.push(new SpacerItem(6));
            contentItems.push(
                new ProgressTrackItem(
                    entry.progressValue!,
                    entry.progressMax!,
                    (t) => t.productionGrid.barHeight,
                ),
            );
            contentItems.push(new SpacerItem(2));
            contentItems.push(new WrappedTextItem(entry.progressLabel ?? '', 'muted', 1));
        }

        const content = new LinearLayoutItem(contentItems, {
            orientation: 'vertical',
            gap: 0,
            width: 'fill',
        });

        return new BoxItem(content, {
            padding: (t) => t.productionGrid.padding,
            fill: (t) => t.colors.tabInactiveFill,
            stroke: (t) => t.colors.tabStroke,
            strokeWidth: 1,
            widthMode: 'fill',
        });
    });
}

function resourceLabel(lang: Language, key: string): string {
    const label = t(lang, `resource.${key}`);
    return label === `resource.${key}` ? key : label;
}

function formatCap(value: number, cap: number): string {
    if (cap <= 0) return `${value}`;
    return `${value}/${cap}`;
}

function formatIo(label: string, value: number, cap: number): string {
    return `${label}: ${formatCap(value, cap)}`;
}

function average(values: number[]): number {
    if (values.length === 0) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function clampProgress(value: number, max: number): number {
    if (max <= 0) return 0;
    return Math.max(0, Math.min(value, max));
}

function averageTaskProgress(
    progress: number[],
    tasks: Array<'tool' | 'weapon'>,
    task: 'tool' | 'weapon',
): { value: number; count: number } {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < progress.length; i += 1) {
        if (tasks[i] !== task) continue;
        sum += progress[i] ?? 0;
        count += 1;
    }
    return { value: count === 0 ? 0 : sum / count, count };
}

function renderForestWork(
    section: ContentBuilder,
    lang: Language,
    state: HexMapState,
    settlement: SettlementView,
): void {
    if (settlement.buildings.lumberjack.qty <= 0) return;
    const storage = getBuildingStorage(settlement, 'lumberjack');
    section.text(t(lang, 'production.forestStock', getTreeTotal(settlement)));
    section.text(t(lang, 'production.campWaiting', storage.wood));
    if (!getLumberCampTarget(state, settlement)) {
        section.text(t(lang, 'production.lumberIdle'));
    }
    section.spacer(6);
}

export function buildProductionTiles(
    lang: Language,
    state: HexMapState,
    settlement: SettlementView,
): ProductionTileEntry[] {
    const woodLabel = resourceLabel(lang, 'wood');
    const planksLabel = resourceLabel(lang, 'planks');
    const charcoalLabel = resourceLabel(lang, 'charcoal');
    const ingotsLabel = resourceLabel(lang, 'ironIngots');
    const leatherLabel = resourceLabel(lang, 'leather');
    const toolsLabel = resourceLabel(lang, 'tools');
    const weaponsLabel = resourceLabel(lang, 'weapons');
    const foodLabel = resourceLabel(lang, 'food');
    const fishLabel = resourceLabel(lang, 'fish');
    const fiberLabel = resourceLabel(lang, 'fiber');
    const clothLabel = resourceLabel(lang, 'clothRope');
    const stoneLabel = resourceLabel(lang, 'stone');
    const bricksLabel = resourceLabel(lang, 'bricks');
    const ironOreLabel = resourceLabel(lang, 'ironOre');
    const goldOreLabel = resourceLabel(lang, 'goldOre');
    const coinsLabel = resourceLabel(lang, 'coins');
    const inputNone = t(lang, 'production.tile.inputNone');
    const lumberTarget = getLumberCampTarget(state, settlement);
    const huntTicks = getHuntTicks(state, settlement);

    const sawmillInputCap = getEffectiveInputCap(settlement, state.config, 'sawmill', 'wood');
    const sawmillOutputCap = getEffectiveOutputCap(settlement, state.config, 'sawmill', 'planks');
    const sawmillWorkMax = state.config.production.sawmillWorkPerBatch;
    const sawmillWork = clampProgress(settlement.production.sawmill.work, sawmillWorkMax);
    const charcoalInputCap = getEffectiveInputCap(settlement, state.config, 'charcoalPit', 'wood');
    const charcoalOutputCap = getEffectiveOutputCap(
        settlement,
        state.config,
        'charcoalPit',
        'charcoal',
    );
    const lumberOutputCap = getEffectiveOutputCap(settlement, state.config, 'lumberjack', 'wood');
    const blacksmithWoodCap = getEffectiveInputCap(settlement, state.config, 'blacksmith', 'wood');
    const blacksmithIngotsCap = getEffectiveInputCap(
        settlement,
        state.config,
        'blacksmith',
        'ironIngots',
    );
    const blacksmithLeatherCap = getEffectiveInputCap(
        settlement,
        state.config,
        'blacksmith',
        'leather',
    );
    const blacksmithToolsCap = getEffectiveOutputCap(
        settlement,
        state.config,
        'blacksmith',
        'tools',
    );
    const blacksmithWeaponsCap = getEffectiveOutputCap(
        settlement,
        state.config,
        'blacksmith',
        'weapons',
    );

    const grid: ProductionTileEntry[] = [];
    if (settlement.buildings.lumberjack.qty > 0) {
        const lumberStorage = getBuildingStorage(settlement, 'lumberjack');
        const lumberEntry: ProductionTileEntry = {
            title: t(lang, 'buildings.label.lumberjackCamp'),
            input: inputNone,
            output: formatIo(woodLabel, lumberStorage.wood, lumberOutputCap),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}lumberjack`,
        };
        grid.push(
            lumberTarget
                ? {
                      ...lumberEntry,
                      progressLabel: t(lang, 'production.lumberWork', lumberTarget.age),
                      progressValue: settlement.production.lumberCamp.work,
                      progressMax: lumberTarget.ticks,
                  }
                : lumberEntry,
        );
    }
    if (settlement.buildings.sawmill.qty > 0) {
        const sawmillStorage = getBuildingStorage(settlement, 'sawmill');
        grid.push({
            title: t(lang, 'production.tile.sawmill'),
            input: formatIo(woodLabel, sawmillStorage.wood, sawmillInputCap),
            output: formatIo(planksLabel, sawmillStorage.planks, sawmillOutputCap),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}sawmill`,
            progressLabel: t(lang, 'production.progress', Math.round(sawmillWork), sawmillWorkMax),
            progressValue: sawmillWork,
            progressMax: sawmillWorkMax,
        });
    }
    if (settlement.buildings.charcoalPit.qty > 0) {
        const charcoalStorage = getBuildingStorage(settlement, 'charcoalPit');
        const charcoalProgress = settlement.production.charcoalPit.progress;
        const charcoalValue = average(charcoalProgress);
        grid.push({
            title: t(lang, 'production.tile.charcoal'),
            input: formatIo(woodLabel, charcoalStorage.wood, charcoalInputCap),
            output: formatIo(charcoalLabel, charcoalStorage.charcoal, charcoalOutputCap),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}charcoal`,
            ...(charcoalProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(charcoalValue),
                          state.config.production.charcoal.ticksPerBatch,
                      ),
                      progressValue: charcoalValue,
                      progressMax: state.config.production.charcoal.ticksPerBatch,
                  }
                : {}),
        });
    }
    if (settlement.buildings.blacksmith.qty > 0) {
        const smithStorage = getBuildingStorage(settlement, 'blacksmith');
        const smithState = settlement.production.blacksmith;
        const smithConfig = state.config.production.blacksmith;
        const toolProgress = averageTaskProgress(smithState.progress, smithState.task, 'tool');
        const weaponProgress = averageTaskProgress(smithState.progress, smithState.task, 'weapon');
        grid.push({
            title: t(lang, 'production.tile.blacksmithTools'),
            input: [
                formatIo(ingotsLabel, smithStorage.ironIngots, blacksmithIngotsCap),
                formatIo(woodLabel, smithStorage.wood, blacksmithWoodCap),
            ].join(', '),
            output: formatIo(toolsLabel, smithStorage.tools.length, blacksmithToolsCap),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}blacksmith`,
            ...(toolProgress.count > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(toolProgress.value),
                          smithConfig.toolTicks,
                      ),
                      progressValue: toolProgress.value,
                      progressMax: smithConfig.toolTicks,
                  }
                : {}),
        });
        grid.push({
            title: t(lang, 'production.tile.blacksmithWeapons'),
            input: [
                formatIo(ingotsLabel, smithStorage.ironIngots, blacksmithIngotsCap),
                formatIo(woodLabel, smithStorage.wood, blacksmithWoodCap),
                formatIo(leatherLabel, smithStorage.leather, blacksmithLeatherCap),
            ].join(', '),
            output: formatIo(weaponsLabel, smithStorage.weapons, blacksmithWeaponsCap),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}blacksmith`,
            ...(weaponProgress.count > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(weaponProgress.value),
                          smithConfig.weaponTicks,
                      ),
                      progressValue: weaponProgress.value,
                      progressMax: smithConfig.weaponTicks,
                  }
                : {}),
        });
    }
    if (settlement.buildings.farm.qty > 0) {
        const farmStorage = getBuildingStorage(settlement, 'farm');
        const farmProgress = settlement.production.farm.progress;
        const farmValue = average(farmProgress);
        grid.push({
            title: t(lang, 'buildings.label.farm'),
            input: inputNone,
            output: formatIo(
                foodLabel,
                farmStorage.food,
                getEffectiveOutputCap(settlement, state.config, 'farm', 'food'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}farm`,
            ...(farmProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(farmValue),
                          state.config.production.farm.ticksPerFarmer,
                      ),
                      progressValue: farmValue,
                      progressMax: state.config.production.farm.ticksPerFarmer,
                  }
                : {}),
        });
    }
    if (settlement.buildings.huntingHut.qty > 0) {
        const huntStorage = getBuildingStorage(settlement, 'huntingHut');
        const huntProgress = settlement.production.hunt.progress;
        const huntValue = average(huntProgress);
        grid.push({
            title: t(lang, 'buildings.label.huntingHut'),
            input: inputNone,
            output: [
                formatIo(
                    foodLabel,
                    huntStorage.food,
                    getEffectiveOutputCap(settlement, state.config, 'huntingHut', 'food'),
                ),
                formatIo(
                    leatherLabel,
                    huntStorage.leather,
                    getEffectiveOutputCap(settlement, state.config, 'huntingHut', 'leather'),
                ),
            ].join(', '),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}hunt`,
            ...(huntProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(huntValue),
                          huntTicks,
                      ),
                      progressValue: huntValue,
                      progressMax: huntTicks,
                  }
                : {}),
        });
    }
    if (settlement.buildings.fishingHut.qty > 0) {
        const fishingStorage = getBuildingStorage(settlement, 'fishingHut');
        const fishingProgress = settlement.production.fishing.progress;
        const fishingValue = average(fishingProgress);
        grid.push({
            title: t(lang, 'buildings.label.fishingHut'),
            input: inputNone,
            output: formatIo(
                fishLabel,
                fishingStorage.fish,
                getEffectiveOutputCap(settlement, state.config, 'fishingHut', 'fish'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}fishing`,
            ...(fishingProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(fishingValue),
                          state.config.production.fishing.ticksPerCatch,
                      ),
                      progressValue: fishingValue,
                      progressMax: state.config.production.fishing.ticksPerCatch,
                  }
                : {}),
        });
    }
    if (settlement.buildings.smokehouse.qty > 0) {
        const smokehouseStorage = getBuildingStorage(settlement, 'smokehouse');
        const smokehouseProgress = settlement.production.smokehouse.progress;
        const smokehouseValue = average(smokehouseProgress);
        grid.push({
            title: t(lang, 'buildings.label.smokehouse'),
            input: formatIo(
                fishLabel,
                smokehouseStorage.fish,
                getEffectiveInputCap(settlement, state.config, 'smokehouse', 'fish'),
            ),
            output: formatIo(
                foodLabel,
                smokehouseStorage.food,
                getEffectiveOutputCap(settlement, state.config, 'smokehouse', 'food'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}smokehouse`,
            ...(smokehouseProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(smokehouseValue),
                          state.config.production.smokehouse.ticksPerBatch,
                      ),
                      progressValue: smokehouseValue,
                      progressMax: state.config.production.smokehouse.ticksPerBatch,
                  }
                : {}),
        });
    }
    if (settlement.buildings.weaver.qty > 0) {
        const weaverStorage = getBuildingStorage(settlement, 'weaver');
        const weaverProgress = settlement.production.weaver.progress;
        const weaverValue = average(weaverProgress);
        grid.push({
            title: t(lang, 'buildings.label.weaver'),
            input: formatIo(
                fiberLabel,
                weaverStorage.fiber,
                getEffectiveInputCap(settlement, state.config, 'weaver', 'fiber'),
            ),
            output: formatIo(
                clothLabel,
                weaverStorage.clothRope,
                getEffectiveOutputCap(settlement, state.config, 'weaver', 'clothRope'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}weaver`,
            ...(weaverProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(weaverValue),
                          state.config.production.weaver.ticksPerBatch,
                      ),
                      progressValue: weaverValue,
                      progressMax: state.config.production.weaver.ticksPerBatch,
                  }
                : {}),
        });
    }
    if (settlement.buildings.quarry.qty > 0) {
        const quarryStorage = getBuildingStorage(settlement, 'quarry');
        const quarryProgress = settlement.production.quarry.progress;
        const quarryValue = average(quarryProgress);
        grid.push({
            title: t(lang, 'buildings.label.quarry'),
            input: inputNone,
            output: formatIo(
                stoneLabel,
                quarryStorage.stone,
                getEffectiveOutputCap(settlement, state.config, 'quarry', 'stone'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}quarry`,
            ...(quarryProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(quarryValue),
                          state.config.production.quarry.ticksPerStone,
                      ),
                      progressValue: quarryValue,
                      progressMax: state.config.production.quarry.ticksPerStone,
                  }
                : {}),
        });
    }
    if (settlement.buildings.masonry.qty > 0) {
        const masonryStorage = getBuildingStorage(settlement, 'masonry');
        const masonryProgress = settlement.production.masonry.progress;
        const masonryValue = average(masonryProgress);
        grid.push({
            title: t(lang, 'buildings.label.masonry'),
            input: formatIo(
                stoneLabel,
                masonryStorage.stone,
                getEffectiveInputCap(settlement, state.config, 'masonry', 'stone'),
            ),
            output: formatIo(
                bricksLabel,
                masonryStorage.bricks,
                getEffectiveOutputCap(settlement, state.config, 'masonry', 'bricks'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}masonry`,
            ...(masonryProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(masonryValue),
                          state.config.production.masonry.ticksPerBrick,
                      ),
                      progressValue: masonryValue,
                      progressMax: state.config.production.masonry.ticksPerBrick,
                  }
                : {}),
        });
    }
    if (settlement.buildings.ironMine.qty > 0) {
        const ironStorage = getBuildingStorage(settlement, 'ironMine');
        const ironProgress = settlement.production.ironMine.progress;
        const ironValue = average(ironProgress);
        grid.push({
            title: t(lang, 'buildings.label.ironMine'),
            input: inputNone,
            output: formatIo(
                ironOreLabel,
                ironStorage.ironOre,
                getEffectiveOutputCap(settlement, state.config, 'ironMine', 'ironOre'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}ironMine`,
            ...(ironProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(ironValue),
                          state.config.production.ironMine.ticksPerOre,
                      ),
                      progressValue: ironValue,
                      progressMax: state.config.production.ironMine.ticksPerOre,
                  }
                : {}),
        });
    }
    if (settlement.buildings.smelter.qty > 0) {
        const smelterStorage = getBuildingStorage(settlement, 'smelter');
        const smelterProgress = settlement.production.smelter.progress;
        const smelterValue = average(smelterProgress);
        grid.push({
            title: t(lang, 'buildings.label.smelter'),
            input: [
                formatIo(
                    ironOreLabel,
                    smelterStorage.ironOre,
                    getEffectiveInputCap(settlement, state.config, 'smelter', 'ironOre'),
                ),
                formatIo(
                    charcoalLabel,
                    smelterStorage.charcoal,
                    getEffectiveInputCap(settlement, state.config, 'smelter', 'charcoal'),
                ),
            ].join(', '),
            output: formatIo(
                ingotsLabel,
                smelterStorage.ironIngots,
                getEffectiveOutputCap(settlement, state.config, 'smelter', 'ironIngots'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}smelter`,
            ...(smelterProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(smelterValue),
                          state.config.production.smelter.ticksPerBatch,
                      ),
                      progressValue: smelterValue,
                      progressMax: state.config.production.smelter.ticksPerBatch,
                  }
                : {}),
        });
    }
    if (settlement.buildings.goldMine.qty > 0) {
        const goldStorage = getBuildingStorage(settlement, 'goldMine');
        const goldProgress = settlement.production.goldMine.progress;
        const goldValue = average(goldProgress);
        grid.push({
            title: t(lang, 'buildings.label.goldMine'),
            input: inputNone,
            output: formatIo(
                goldOreLabel,
                goldStorage.goldOre,
                getEffectiveOutputCap(settlement, state.config, 'goldMine', 'goldOre'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}goldMine`,
            ...(goldProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(goldValue),
                          state.config.production.goldMine.ticksPerOre,
                      ),
                      progressValue: goldValue,
                      progressMax: state.config.production.goldMine.ticksPerOre,
                  }
                : {}),
        });
    }
    if (settlement.buildings.mint.qty > 0) {
        const mintStorage = getBuildingStorage(settlement, 'mint');
        const mintProgress = settlement.production.mint.progress;
        const mintValue = average(mintProgress);
        grid.push({
            title: t(lang, 'buildings.label.mint'),
            input: formatIo(
                goldOreLabel,
                mintStorage.goldOre,
                getEffectiveInputCap(settlement, state.config, 'mint', 'goldOre'),
            ),
            output: formatIo(
                coinsLabel,
                mintStorage.coins,
                getEffectiveOutputCap(settlement, state.config, 'mint', 'coins'),
            ),
            settingsActionId: `${ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX}mint`,
            ...(mintProgress.length > 0
                ? {
                      progressLabel: t(
                          lang,
                          'production.progress',
                          Math.round(mintValue),
                          state.config.production.mint.ticksPerBatch,
                      ),
                      progressValue: mintValue,
                      progressMax: state.config.production.mint.ticksPerBatch,
                  }
                : {}),
        });
    }

    return grid;
}

function renderProductionStorage(
    section: ContentBuilder,
    lang: Language,
    state: HexMapState,
    settlement: SettlementView,
): void {
    section.text(t(lang, 'production.storage.title'), 'section');
    const grid = buildProductionTiles(lang, state, settlement);
    if (grid.length > 0) {
        const cells = buildProductionCells(grid);
        section.item(
            new GridLayoutItem(cells, {
                columns: (t) => t.productionGrid.columns,
                gap: (t) => t.productionGrid.gap,
                padding: 0,
                childWidthMode: 'fill',
                alignY: 'start',
            }),
        );
    }
    section.spacer(6);
}

function renderPopulationStatus(
    section: ContentBuilder,
    lang: Language,
    state: HexMapState,
    settlement: SettlementView,
): void {
    section.progress(
        t(lang, 'production.populationGrowth'),
        settlement.production.population.growth,
        1,
    );
    section.text(
        t(
            lang,
            'production.foodHours',
            getFoodHoursSupply(settlement, state.config),
            settlement.production.population.houseFoodTargetHours,
        ),
    );
    const coldDebt = Math.max(0, getMaxWarmth() - getColdestWarmth(settlement));
    section.text(t(lang, 'production.coldDebt', coldDebt));
    section.spacer(6);
}

function renderHunt(section: ContentBuilder, lang: Language, settlement: SettlementView): void {
    if (settlement.buildings.huntingHut.qty <= 0) return;
    section.text(t(lang, 'production.wildlife', getWildlifeTotal(settlement)));
    section.spacer(6);
}

function renderFishing(section: ContentBuilder, lang: Language, settlement: SettlementView): void {
    if (settlement.buildings.fishingHut.qty <= 0) return;
    section.text(
        t(
            lang,
            'production.fishStock',
            Math.round(settlement.regionResources.fish),
            Math.round(settlement.regionResources.fishCap),
        ),
    );
    section.spacer(6);
}

function renderBlacksmith(
    section: ContentBuilder,
    lang: Language,
    settlement: SettlementView,
): void {
    if (settlement.buildings.blacksmith.qty <= 0) return;
    section.text(
        t(lang, 'production.blacksmithTargetTools', settlement.production.blacksmith.targetTools),
    );
    section.button(
        ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET,
        t(lang, 'production.setBlacksmithToolsTarget'),
        true,
    );
    section.text(
        t(
            lang,
            'production.blacksmithTargetWeapons',
            settlement.production.blacksmith.targetWeapons,
        ),
    );
    section.button(
        ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET,
        t(lang, 'production.setBlacksmithWeaponsTarget'),
        true,
    );
    section.spacer(4);

    section.spacer(6);
}

export function renderProductionTab(
    builder: ContentBuilder,
    state: HexMapState,
    settlement: SettlementView,
): void {
    const lang = state.config.ui.language;
    builder.section(t(lang, 'production.section'), (section) => {
        renderForestWork(section, lang, state, settlement);
        renderProductionStorage(section, lang, state, settlement);
        renderPopulationStatus(section, lang, state, settlement);
        renderHunt(section, lang, settlement);
        renderFishing(section, lang, settlement);
        renderBlacksmith(section, lang, settlement);
    });
}
